import { useMemo, useState } from "react";
import { MOVEMENT_TYPES, allocateFifo, isActive, portalLabel } from "../../services/payrollFinanceCalculator";
import { recordMovement, rejectMovement } from "../../services/payrollFinanceService";
import { PortalSelect, accountSuggestions, dateLabel, money, todayIso } from "./shared";

const CHARGE_TYPES = ["Bank charges", "IBFT / transfer fee", "Withholding tax", "Account maintenance", "Other"];

const emptyForm = (type, portal) => ({
  type, portal: portal === "ALL" ? "ALM" : portal, employeeId: "", date: todayIso(), time: "", amount: "",
  companyAccount: "", chargeType: "Bank charges", reference: "", note: ""
});

export default function TransfersTab({ data, position, portal, reload, notify }) {
  const [form, setForm] = useState(null);
  const set = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));

  const holders = useMemo(() => {
    if (!form) return [];
    return position.rows.filter((row) => row.withdrawal.portal === form.portal && row.withEmployee > 0);
  }, [form, position.rows]);

  const employeesHolding = useMemo(() => {
    const byEmployee = new Map();
    holders.forEach((row) => {
      const entry = byEmployee.get(row.withdrawal.employeeId) || { id: row.withdrawal.employeeId, name: row.withdrawal.employeeName, held: 0 };
      entry.held += row.withEmployee;
      byEmployee.set(row.withdrawal.employeeId, entry);
    });
    return [...byEmployee.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [holders]);

  const preview = useMemo(() => {
    if (!form || !form.employeeId || !(Number(form.amount) > 0)) return null;
    const items = holders.filter((row) => row.withdrawal.employeeId === form.employeeId).map((row) => ({ id: row.withdrawal.id, open: row.withEmployee, row }));
    const result = allocateFifo(items, Number(form.amount));
    return { ...result, lines: result.allocations.map((a) => ({ ...a, row: items.find((item) => item.id === a.withdrawalId).row })) };
  }, [form, holders]);

  const pickEmployee = (e) => {
    const id = e.target.value;
    const held = employeesHolding.find((employee) => employee.id === id)?.held;
    setForm((current) => ({ ...current, employeeId: id, amount: current.type === MOVEMENT_TYPES.TO_ALM && held ? String(held) : current.amount }));
  };

  const submit = async (event) => {
    event.preventDefault();
    try {
      const result = await recordMovement(form);
      notify(`${form.type === MOVEMENT_TYPES.TO_ALM ? "Transfer to ALM" : "Charge"} recorded.${result.warning ? ` ${result.warning}` : ""}`, Boolean(result.warning));
      setForm(null);
      await reload();
    } catch (err) { notify(err.message || "Could not record this entry.", true); }
  };

  const reject = async (movement) => {
    const reason = window.prompt(`Reason for rejecting this ${money(movement.amount)} ${movement.type === MOVEMENT_TYPES.TO_ALM ? "transfer" : "charge"}:`);
    if (reason === null) return;
    try { await rejectMovement(movement, reason); notify("Entry rejected."); await reload(); }
    catch (err) { notify(err.message || "Could not reject.", true); }
  };

  const movements = data.movements
    .filter((m) => portal === "ALL" || m.portal === portal)
    .sort((a, b) => `${b.date} ${b.time || ""}`.localeCompare(`${a.date} ${a.time || ""}`));

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title">Transfers to ALM &amp; charges</h2>
        <div className="button-row">
          <button type="button" className="btn btn-primary" onClick={() => setForm(emptyForm(MOVEMENT_TYPES.TO_ALM, portal))}>Employee → ALM Account</button>
          <button type="button" className="btn btn-muted" onClick={() => setForm(emptyForm(MOVEMENT_TYPES.CHARGE, portal))}>Charge Deducted</button>
        </div>
      </div>

      {form && (
        <form className="panel-body" onSubmit={submit}>
          <h3 style={{ marginTop: 0 }}>{form.type === MOVEMENT_TYPES.TO_ALM ? "Employee transferred money to an ALM company account" : "Charge deducted from the employee's account"}</h3>
          <div className="form-grid">
            <PortalSelect value={form.portal} onChange={(value) => setForm({ ...form, portal: value, employeeId: "", amount: "" })} />
            <div className="field"><label>Employee account</label>
              <select className="input" value={form.employeeId} onChange={pickEmployee}>
                <option value="">Select employee holding money…</option>
                {employeesHolding.map((employee) => <option key={employee.id} value={employee.id}>{employee.name} — holds {money(employee.held)}</option>)}
              </select>
            </div>
            <div className="field"><label>Amount (PKR)</label><input className="input" type="number" min="0.01" step="0.01" value={form.amount} onChange={set("amount")} /></div>
            <div className="field"><label>Date</label><input className="input" type="date" value={form.date} onChange={set("date")} /></div>
            <div className="field"><label>Time</label><input className="input" type="time" value={form.time} onChange={set("time")} /></div>
            {form.type === MOVEMENT_TYPES.TO_ALM ? (
              <div className="field"><label>Received in ALM account</label>
                <input className="input" list="pf-accounts" value={form.companyAccount} onChange={set("companyAccount")} placeholder="e.g. HBL — ALM Outsourcing" />
                <datalist id="pf-accounts">{accountSuggestions(data).map((name) => <option key={name} value={name} />)}</datalist>
              </div>
            ) : (
              <div className="field"><label>Charge for</label>
                <input className="input" list="pf-charge-types" value={form.chargeType} onChange={set("chargeType")} />
                <datalist id="pf-charge-types">{CHARGE_TYPES.map((name) => <option key={name} value={name} />)}</datalist>
              </div>
            )}
            <div className="field"><label>Bank reference</label><input className="input" value={form.reference} onChange={set("reference")} /></div>
            <div className="field field-wide"><label>Note</label><input className="input" value={form.note} onChange={set("note")} /></div>
          </div>
          {preview && (
            <p className="page-copy" style={{ marginTop: 12 }}>
              Applied to this employee's withdrawals, oldest first: {preview.lines.map((line) => `${dateLabel(line.row.withdrawal.date)} ${money(line.amount)}`).join(" · ") || "—"}
              {preview.unallocated > 0 && <strong style={{ color: "var(--danger)" }}> — {money(preview.unallocated)} is more than the employee still holds.</strong>}
            </p>
          )}
          {form.type === MOVEMENT_TYPES.CHARGE && <p className="page-copy">Charges are also added to Operating Expenses as a draft Borrowing Cost entry for you to approve.</p>}
          <div className="button-row" style={{ marginTop: 16 }}>
            <button className="btn btn-primary" type="submit">Save</button>
            <button className="btn btn-muted" type="button" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="panel-body table-wrap">
        <table className="data-table">
          <thead><tr><th>Date / time</th><th>Portal</th><th>Type</th><th>Employee</th><th>To ALM account / charge for</th><th>Reference</th><th>Amount</th><th /></tr></thead>
          <tbody>
            {movements.map((m) => {
              const rejected = !isActive(m);
              return (
                <tr key={m.id} style={rejected ? { opacity: 0.55, textDecoration: "line-through" } : undefined}>
                  <td>{dateLabel(m.date, m.time)}</td><td>{portalLabel(m.portal)}</td>
                  <td>{m.type === MOVEMENT_TYPES.TO_ALM ? "Employee → ALM" : "Charge"}</td><td>{m.employeeName}</td>
                  <td>{m.type === MOVEMENT_TYPES.TO_ALM ? m.companyAccount : m.chargeType}{m.note && <div className="page-copy" style={{ fontSize: 12 }}>{m.note}</div>}{rejected && <div style={{ fontSize: 12, textDecoration: "none" }}>Rejected: {m.rejectedReason}</div>}</td>
                  <td>{m.reference || "—"}</td><td>{money(m.amount)}</td>
                  <td>{!rejected && <button className="btn btn-danger" type="button" onClick={() => reject(m)}>Reject</button>}</td>
                </tr>
              );
            })}
            {!movements.length && <tr><td colSpan={8}>No transfers or charges recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
